import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { USER_ROLES } from '../../../utils/constants';
import leadService from '../../../api/crm/leads/leadService';
import {
  Eye,
  AlertCircle,
  Users,
  Calendar,
  Mail,
  Phone,
  Building,
  Search
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import { toast } from 'react-hot-toast';

const PendingVideoVerificationLeads = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const requestIdRef = useRef(0);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalLeads: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });

  // Check if user is superadmin
  useEffect(() => {
    if (user && user.role !== USER_ROLES.SUPER_ADMIN) {
      toast.error('Access denied. This page is for superadmin only.');
      navigate(-1);
    }
  }, [user, navigate]);

  // Load leads with proper pagination
  const loadLeadsData = useCallback(async (page, limit, search = '') => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    try {
      setLoading(true);
      setError(null);

      const filters = {
        hasPendingVideoVerification: true,
        page: page,
        limit: limit,
        search: search || undefined,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      };

      // Remove undefined values
      Object.keys(filters).forEach(key =>
        filters[key] === undefined && delete filters[key]
      );

      const result = await leadService.getAllLeads(filters);
      if (requestId !== requestIdRef.current) return;

      if (result.success) {
        setLeads(result.leads || []);
        setPagination(result.pagination || {
          currentPage: page,
          totalPages: 1,
          totalLeads: result.leads?.length || 0,
          hasNextPage: false,
          hasPrevPage: false,
          limit: limit
        });
      } else {
        setError(result.error || 'Failed to load leads');
        setLeads([]);
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(err.message || 'Failed to load leads');
      setLeads([]);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Keep search responsive without firing duplicate requests on every keypress.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Load leads from API when pagination or debounced search changes.
  useEffect(() => {
    loadLeadsData(currentPage, itemsPerPage, debouncedSearchQuery);
  }, [currentPage, itemsPerPage, debouncedSearchQuery, loadLeadsData]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle viewing lead details with documents tab
  const handleViewLead = (leadId) => {
    navigate(`/lead_detail_360/${leadId}?tab=documents`);
  };

  // Format phone number
  const formatPhone = (phone) => {
    if (!phone) return 'N/A';
    return phone.length === 10 ? `+91 ${phone.slice(0, 5)} ${phone.slice(5)}` : phone;
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get full lead name
  const getLeadName = (lead) => {
    if (lead.firstName && lead.lastName) {
      return `${lead.firstName} ${lead.lastName}`;
    }
    return lead.firstName || lead.lastName || 'Unknown';
  };

  // Get assignee details
  const getAssigneeDetails = (lead) => {
    const name = lead.owner || (lead.assignedTo && typeof lead.assignedTo === 'object' ? (lead.assignedTo.name || `${lead.assignedTo.firstName || ''} ${lead.assignedTo.lastName || ''}`.trim()) : null) || 'Unassigned';
    const email = lead.ownerEmail || (lead.assignedTo && typeof lead.assignedTo === 'object' ? lead.assignedTo.email : null) || null;
    return { name, email };
  };

  if (user && user.role !== USER_ROLES.SUPER_ADMIN) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Pending Video Verification</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Leads with video documents awaiting verification</p>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex gap-4 items-center">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by Lead ID, name, email, company, or phone..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-4">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-red-900">Error loading leads</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => loadLeadsData(currentPage, itemsPerPage, searchQuery)}
              className="mt-3"
            >
              Retry
            </Button>
          </div>
        </div>
      )}

      {!error && (
        <>
          {/* Leads Table */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading leads...</p>
                </div>
              </div>
            )}

            {!loading && (!leads || leads.length === 0) && (
              <div className="px-6 py-12 text-center">
                <div className="text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-lg font-medium">No Pending Verifications</p>
                  <p className="text-sm mt-2">All leads with video documents have completed verification.</p>
                </div>
              </div>
            )}

            {!loading && leads && leads.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-[1200px] divide-y divide-gray-200 table-auto w-full">
                  <thead className="bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                        Lead ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[260px] max-w-[300px]">
                        Lead Details
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">
                        Contact
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[220px] max-w-[260px]">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px] max-w-[220px]">
                        Assignee
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[190px]">
                        Created
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[110px]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {leads.map((lead) => (
                      <tr
                        key={lead._id || lead.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-4 whitespace-nowrap min-w-[120px]">
                          {lead.leadId ? (
                            <button
                              type="button"
                              onClick={() => handleViewLead(lead._id || lead.id)}
                              className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline tabular-nums"
                              title={`View Lead #${lead.leadId}`}
                            >
                              {lead.leadId}
                            </button>
                          ) : (
                            <span className="text-sm font-semibold text-gray-900 tabular-nums">N/A</span>
                          )}
                        </td>
                        <td className="px-4 py-4 min-w-[260px] max-w-[300px]">
                          <div className="min-w-0">
                            <button
                              type="button"
                              onClick={() => handleViewLead(lead._id || lead.id)}
                              className="text-sm font-semibold text-gray-900 hover:text-agrosaf-green hover:underline text-left leading-5 break-words"
                              title={getLeadName(lead)}
                            >
                              {getLeadName(lead)}
                            </button>
                            <div className="flex items-start gap-2 mt-1 text-sm text-gray-500">
                              <Building size={14} className="mt-0.5 text-gray-400 flex-shrink-0" />
                              <span className="leading-5 break-words">{lead.company || 'N/A'}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[150px]">
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <Phone size={15} className="text-gray-400 flex-shrink-0" />
                            <span className="tabular-nums">{formatPhone(lead.phone)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[220px] max-w-[260px]">
                          <div className="flex items-center gap-2 text-sm text-gray-700 min-w-0">
                            <Mail size={15} className="text-gray-400 flex-shrink-0" />
                            <span className="truncate" title={lead.email || 'N/A'}>{lead.email || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[180px] max-w-[220px]">
                          {(() => {
                            const assignee = getAssigneeDetails(lead);
                            return (
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-gray-900 truncate capitalize" title={assignee.name}>
                                  {assignee.name}
                                </div>
                                {assignee.email && (
                                  <div className="text-xs text-gray-500 truncate" title={assignee.email}>
                                    {assignee.email}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[190px]">
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <Calendar size={15} className="text-gray-400 flex-shrink-0" />
                            <span>{formatDate(lead.createdAt)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium min-w-[110px]">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewLead(lead._id || lead.id)}
                              className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-semibold rounded-md border border-blue-100 transition-colors whitespace-nowrap"
                              title="View documents for verification"
                            >
                              <Eye size={14} />
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

          </div>

          {Number(pagination?.totalPages || 1) > 1 && (
            <PaginationControls
              currentPage={pagination.currentPage || currentPage}
              totalPages={pagination.totalPages || 1}
              totalItems={Number(pagination.totalLeads || leads.length || 0)}
              limit={pagination.limit || itemsPerPage}
              hasNextPage={pagination.hasNextPage}
              hasPrevPage={pagination.hasPrevPage}
              onPageChange={handlePageChange}
              onLimitChange={handleItemsPerPageChange}
              limitOptions={[3, 10, 25, 50, 100]}
            />
          )}
        </>
      )}
    </div>
  );
};

export default PendingVideoVerificationLeads;
